import { useCallback, useEffect, useRef } from 'react';
import { generalSettingStorage, progressStorage } from 'src/utils/StorageUtils';
import { defaultGeneralSettings } from '../../store/slices/generalSettingsSlice';
import { store } from '../../store/store';
import { useNote } from '../components/useNote';
import { useLearning } from '../data/useLearning';
import useProgress from '../data/useProgress';
import { useSocial } from '../data/useSocial';
import useUser from '../data/useUser';
import { useWordList } from '../data/useWordList';
import { useWordTranslate } from '../data/useWordTranslate';
import { useDeviceInfo } from './useDeviceInfo';

const TEN_MINUTES = 10 * 60 * 1000;

// Global flags to prevent multiple instances
let globalMessageHandlerSetUp = false;
let globalWorkerRegistration: ServiceWorkerRegistration | null = null;
let globalIsRegistering = false;
let globalMessageHandler: ((event: MessageEvent) => void) | null = null; // Store the global message handler

export const useServiceWorker = (register: boolean = true) => {
    const { fetchUserData } = useUser(false);
    const { fetchLearningData } = useLearning(false);
    const { uploadProgress, uploadProgressAtomic } = useProgress();
    const { fetchTranslates } = useWordTranslate(false);
    const { fetchNoteData } = useNote(false);
    const { fetchSocialInfo } = useSocial(false);
    const { fetchAllWordListData } = useWordList();
    const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
    const isInitialized = useRef(false);
    const { sendDeviceInfo } = useDeviceInfo();

    const updateData = useCallback(async (token: string) => {
        try {
            await uploadProgressAtomic();
            const [user, learning] =
                await Promise.all([
                    fetchUserData(token!),
                    fetchLearningData(token!),
                ]);
            //  console.log('user', user);
            await Promise.all([
                fetchTranslates(user?.NativeLanguageCode),
                fetchNoteData(user?.UserId),
                fetchSocialInfo(user?.UserId!, user?.CountryCode),
                fetchAllWordListData(learning?.WordLists || []),
            ]);

            // Update last update timestamp
            const currentSettings = generalSettingStorage.get() || defaultGeneralSettings;
            generalSettingStorage.set({
                ...currentSettings,
                ServiceWorkerLastUpdate: Date.now().toString()
            });
        } catch (error) {
            console.error('Error updating auto user data:', error);
        }
    }, [fetchUserData, fetchLearningData, fetchNoteData,
        fetchAllWordListData, uploadProgressAtomic, fetchTranslates, fetchSocialInfo]);

    const checkLastUpdate = useCallback((currentUserInfo: any) => {
        const currentSettings = generalSettingStorage.get();
        const lastUpdate = currentSettings?.ServiceWorkerLastUpdate;
        const now = Date.now();

        // If we're using current timestamp, store it
        if (!lastUpdate) {
            const settings = currentSettings || defaultGeneralSettings;
            generalSettingStorage.set({
                ...settings,
                ServiceWorkerLastUpdate: now.toString()
            });
            return;
        }


        const timestamp = parseInt(lastUpdate);

        // Only request update if 10 minutes have passed
        if ((now - timestamp) >= TEN_MINUTES && currentUserInfo?.Token) {
            updateData(currentUserInfo.Token);
            sendDeviceInfo(false);
        }
    }, [updateData, sendDeviceInfo]);




    const checkProgress = useCallback((currentUserInfo: any) => {
        console.log('checkProgress');
        const progressFromStorage = progressStorage.get();
        if (progressFromStorage && progressFromStorage?.length > 0 && currentUserInfo?.Token) {
            uploadProgress(progressFromStorage);
        }
    }, [uploadProgress]);

    const setupMessageHandler = useCallback(() => {
        if (globalMessageHandlerSetUp && globalMessageHandler) {
            console.log('Service Worker message handler already set up globally, skipping...');
            return;
        }

        // Remove any existing message handler first
        if (globalMessageHandler) {
            navigator.serviceWorker.removeEventListener('message', globalMessageHandler);
        }

        const messageHandler = (event: MessageEvent) => {
            const { type } = event.data;
            const currentUserInfo = store.getState().user.userInfo;
            const currentLearningInfo = store.getState().learning.learningInfo;

            if (type === 'CHECK_DATA') {
                //  checkReminder(currentUserInfo);
                checkProgress(currentUserInfo);
                checkLastUpdate(currentUserInfo);
            } else if (type === 'REQUEST_USER_DATA') {
                console.log('Service Worker: Received REQUEST_USER_DATA message');
                // Send current user and learning data to service worker
                if (globalWorkerRegistration?.active && currentUserInfo) {
                    globalWorkerRegistration.active.postMessage({
                        type: 'SET_DATA',
                        userData: currentUserInfo,
                        learningData: currentLearningInfo
                    });
                    console.log('Service Worker: Sent user data to service worker');
                }
            } else if (type === 'DATA_SYNC') {
                console.log('Service Worker: Received data sync from service worker');
                // Handle data sync from service worker if needed
            }
        };

        globalMessageHandler = messageHandler;
        messageHandlerRef.current = messageHandler;
        navigator.serviceWorker.addEventListener('message', messageHandler);
        globalMessageHandlerSetUp = true;
        console.log('Service Worker message handler set up');
    }, [checkLastUpdate, checkProgress]);



    const unregisterWorker = useCallback(async () => {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
            }
            globalWorkerRegistration = null;
            globalIsRegistering = false;
            globalMessageHandlerSetUp = false;
            // Don't reload the page - keep service worker running for notifications
        }
    }, []);

    const registerWorker = useCallback(async () => {
        if (!('serviceWorker' in navigator)) {
            console.warn('Service workers are not supported in this browser');
            return;
        }

        // Prevent multiple simultaneous registrations
        if (globalIsRegistering) {
            console.log('Service worker registration already in progress, skipping...');
            return;
        }

        // Check if already registered and active
        if (globalWorkerRegistration?.active) {
            console.log('Service worker already registered and active, skipping...');
            return;
        }

        globalIsRegistering = true;
        console.log('Attempting to register service worker...');

        try {
            const registration = await navigator.serviceWorker.register('/serviceWorker.js', {
                scope: '/'
            });
            console.log('Service worker registration successful:', registration);

            // Wait for the service worker to be active
            if (registration.active) {
                console.log('Service worker is already active');
                globalWorkerRegistration = registration;
                setupMessageHandler();

            } else {
                console.log('Waiting for service worker to activate...');
                registration.addEventListener('activate', () => {
                    console.log('Service worker activated');
                    globalWorkerRegistration = registration;
                    setupMessageHandler();
                });
            }

            // Check for updates
            registration.addEventListener('updatefound', () => {
                console.log('Service worker update found');
                const newWorker = registration.installing;
                if (newWorker) {
                    newWorker.addEventListener('statechange', () => {
                        console.log('Service worker state changed:', newWorker.state);
                        if (newWorker.state === 'activated') {
                            globalWorkerRegistration = registration;
                            setupMessageHandler();
                        }
                    });
                }
            });

            // Handle service worker errors
            registration.addEventListener('error', (error) => {
                console.error('Service worker error:', error);
                // Attempt to unregister and re-register on error
                unregisterWorker().then(() => {
                    setTimeout(registerWorker, 5000); // Retry after 5 seconds
                });
            });

        } catch (error) {
            console.error('Service worker registration failed:', error);
            globalIsRegistering = false;
            // Retry registration after a delay
            setTimeout(registerWorker, 5000);
        }
    }, [setupMessageHandler, unregisterWorker]);

    // Initialize service worker on mount (only once)
    useEffect(() => {
        if (!register) return;

        const initializeWorker = async () => {
            if ('serviceWorker' in navigator) {
                console.log('Initializing service worker...');

                // Always unregister and re-register for fresh start
                await unregisterWorker();

                // Small delay to ensure cleanup
                await new Promise(resolve => setTimeout(resolve, 100));

                await registerWorker();

                isInitialized.current = true;
            }
        };

        initializeWorker();
    }, []); // Empty dependency array - only run once on mount

    const updateServiceWorkerUserData = useCallback((userData: any, learningData: any) => {
        if (globalWorkerRegistration?.active) {
            globalWorkerRegistration.active.postMessage({ type: 'SET_DATA', userData, learningData });
        }
    }, []);

    return { registerWorker, unregisterWorker, updateServiceWorkerUserData };
};
